import React, { useMemo } from "react";

import { type ChartOptions, type TooltipItem } from "chart.js";
import { Pie } from "react-chartjs-2";

import { useUser } from "@components/user-context";
import { formatting } from "@north-beam/nb-common";

import { chartTooltip } from "./chart-tooltip";
import CustomLegend from "./custom-legend";

export type PieChartData = {
  label: string;
  value: number;
  color: string;
};

type Props = {
  data: PieChartData[];
  size?: number;
  showLegend?: boolean;
  showTooltip?: boolean;
  cutout?: string;
  borderWidth?: number;
  hoverBorderWidth?: number;
  noDataColor?: string;
  className?: string;
  legendHeight?: number;
  format?: "dollars" | "percentage" | "decimal" | "multiplier" | "integer";
  animation?: boolean;
};

export const PieChart: React.FC<Props> = ({
  data,
  size = 160,
  showLegend = false,
  showTooltip = false,
  cutout = "0%",
  borderWidth = 2,
  hoverBorderWidth = 2,
  noDataColor = "#E0E0E0",
  className,
  legendHeight = 40,
  format = "decimal",
  animation = true,
}) => {
  const { user } = useUser();

  const chartData = useMemo(() => {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    const hasData = total > 0;

    return {
      labels: hasData ? data.map((item) => item.label) : ["No Data"],
      datasets: [
        {
          data: hasData ? data.map((item) => item.value) : [100],
          backgroundColor: hasData
            ? data.map((item) => item.color)
            : [noDataColor],
          borderColor: "#ffffff",
          borderWidth,
          hoverOffset: 0,
          hoverBorderWidth,
        },
      ],
    };
  }, [data, borderWidth, hoverBorderWidth, noDataColor]);

  const options = useMemo<ChartOptions<"pie">>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: animation ? undefined : false,
      cutout,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
          external: showTooltip ? chartTooltip : undefined,
          callbacks: {
            label(context: TooltipItem<"pie">) {
              const value = formatting.formatMetric(context.parsed, format, {
                code: user.currencies.currentCurrency,
                precision: "approximate",
              });

              return `${context.label}: ${value}`;
            },
          },
        },
      },
      layout: {
        padding: 0,
      },
    }),
    [showTooltip, cutout, user.currencies.currentCurrency, format],
  );

  const totalHeight = size + (showLegend ? legendHeight : 0);

  return (
    <div
      className={className}
      style={{
        height: `${totalHeight}px`,
        width: `${size}px`,
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <div
        style={{
          height: `${size}px`,
          width: `${size}px`,
          position: "relative",
          borderRadius: "50%",
          boxShadow: "0 0 0 4px white",
          filter: "drop-shadow(0px 0px 20px rgba(0, 0, 0, 0.10))",
          flexShrink: 0,
        }}
      >
        <Pie data={chartData} options={options} />
      </div>
      {showLegend && <CustomLegend data={data} noDataColor={noDataColor} />}
    </div>
  );
};
